import { defineStore } from 'pinia'
import type { SearchJudgmentOverviewParams } from '@/types/api'

interface SelectionState {
  selectedAsin: string | null
  selectedSellerId: string | null
  selectedNgWord: string | null
  selectedTaxId: number | null
  checkedKeys: Set<string> // `${sellerId}:${asin}` 形式
  statusFilter: string // 'all' | JudgmentStatusCode
  premiumOnly: boolean
  filters: SearchJudgmentOverviewParams
  /**
   * 絞り込みタグに出す表示用の値。
   * 例: セラーIDは検索には内部ID(o.SellerId)を使うが、画面には別名(SellerIdAlias)を
   * 出したいので、キーごとの表示文字列をここに持たせる。
   */
  filterDisplay: Record<string, string>
  /** 並べ替え。サーバー側 ORDER BY に渡すため、状態はここで持つ */
  sortKey: string
  sortDir: 'asc' | 'desc'
}

/** 絞り込みタグのラベル。表示順もこの定義順に従う */
/** 最初に押したとき降順(新しい順)で並べる列 */
const DESC_FIRST_SORT_KEYS = new Set(['createDate', 'updateDate', 'judgeDate'])

const FILTER_LABELS: { key: keyof SearchJudgmentOverviewParams; label: string }[] = [
  { key: 'sellerId', label: 'セラー' },
  { key: 'brand', label: 'ブランド' },
  { key: 'parentAsin', label: '親ASIN' },
  { key: 'childAsin', label: '子ASIN' },
  { key: 'productName', label: '商品名' },
]

export interface FilterTag {
  key: string
  label: string
  value: string
}

export const useSelectionStore = defineStore('selection', {
  state: (): SelectionState => ({
    selectedAsin: null,
    selectedSellerId: null,
    selectedNgWord: null,
    selectedTaxId: null,
    checkedKeys: new Set(),
    // VB.NET版は ComboBoxRunJudgeStatus.SelectedIndex = 3、
    // つまりマスタ順で「審査中（審査）」が初期選択だった。それに合わせる。
    // (「すべて」だと67万件が対象になり、審査業務の入口として実用的でない)
    statusFilter: '1',
    premiumOnly: true, // VB.NET版のCheckBoxFilterPremiumPlanはデフォルトON
    filters: { judgmentSubStatusCode: '2' },
    filterDisplay: {},
    sortKey: 'updateDate',
    sortDir: 'desc',
  }),

  getters: {
    checkedCount: (state) => state.checkedKeys.size,

    /**
     * 現在有効な絞り込み条件をタグ用に列挙する。
     * 何で絞られているのかが一覧の上で分かり、個別に外せるようにするためのもの。
     */
    filterTags: (state): FilterTag[] => {
      const tags: FilterTag[] = []
      for (const { key, label } of FILTER_LABELS) {
        const raw = state.filters[key]
        if (raw === undefined || raw === null || raw === '') continue
        tags.push({ key, label, value: state.filterDisplay[key] ?? String(raw) })
      }
      if (state.filters.includeDeleted) {
        tags.push({ key: 'includeDeleted', label: '', value: '一時停止・削除を含む' })
      }
      if (state.filters.includeDisabled) {
        tags.push({ key: 'includeDisabled', label: '', value: '販売無効化中を含む' })
      }
      if (state.premiumOnly) {
        tags.push({ key: 'premiumOnly', label: '', value: 'プレミアムのみ' })
      }
      // 登録日は From/To を1つのタグにまとめる(片方だけの指定も許容)
      const f = state.filters.createDateFrom
      const t = state.filters.createDateTo
      if (f || t) {
        tags.push({
          key: 'createDate',
          label: '登録日',
          value: f && t ? (f === t ? f : `${f} 〜 ${t}`) : f ? `${f} 以降` : `${t} まで`,
        })
      }
      if (state.filters.judgedFilter) {
        tags.push({
          key: 'judgedFilter',
          label: '',
          value: state.filters.judgedFilter === 'rejudge' ? '再審査のみ' : '新規のみ',
        })
      }
      return tags
    },
    effectiveParams: (state): SearchJudgmentOverviewParams => ({
      ...state.filters,
      judgmentStatusCode: state.statusFilter === 'all' ? undefined : state.statusFilter,
      memberPlan: state.premiumOnly ? '1' : undefined, // MEMBER_PLAN_PREMIUM = '1'
      sortKey: state.sortKey,
      sortDir: state.sortDir,
    }),
  },

  actions: {
    select(sellerId: string, asin: string) {
      this.selectedSellerId = sellerId
      this.selectedAsin = asin
      this.selectedNgWord = null
    },
    /**
     * 一覧のセルをダブルクリックしたときの絞り込み。
     * 既存の条件は消さずに追加する(複数条件で絞り込めるようにするため)。
     */
    applyQuickFilter(
      key: keyof SearchJudgmentOverviewParams,
      value: string,
      display?: string,
    ) {
      if (!value) return
      this.filters = { ...this.filters, [key]: value }
      this.filterDisplay = { ...this.filterDisplay, [key]: display ?? value }
    },

    /** 絞り込みタグの「✕」。その条件だけを外し、残りの条件で絞り込み直す */
    removeFilter(key: string) {
      if (key === 'premiumOnly') {
        this.premiumOnly = false
        return
      }
      // 登録日はFrom/Toの2キーを1タグで表しているので、まとめて外す
      if (key === 'createDate') {
        const next = { ...this.filters }
        delete next.createDateFrom
        delete next.createDateTo
        this.filters = next
        return
      }
      const next = { ...this.filters } as Record<string, unknown>
      delete next[key]
      this.filters = next as SearchJudgmentOverviewParams
      const nextDisplay = { ...this.filterDisplay }
      delete nextDisplay[key]
      this.filterDisplay = nextDisplay
    },

    /**
     * すべての絞り込みを解除する。
     * premiumOnly は filters の外にあるが、タグとしては同列に並ぶため
     * ここでも一緒に解除して挙動を揃える。
     */
    clearAllFilters() {
      this.filters = {}
      this.filterDisplay = {}
      this.premiumOnly = false
    },

    /**
     * 列ヘッダーの並べ替え。同じ列を押すたびに昇順⇔降順を切り替える(VB版の一覧と同じ)。
     * 以前は「昇順→降順→既定(更新日の降順)」で循環していたが、3回目に別の列の並びに
     * 変わってしまい分かりにくいため、既定へは戻さない。
     * 別の列を押したときの最初の向きは、日付の列は降順(新しい順)、それ以外は昇順。
     * 一覧はサーバー側で件数を打ち切るため、並べ替えもサーバーに任せないと
     * 「取得済みの中の最古」といった誤った結果になる。
     */
    toggleSort(key: string) {
      if (this.sortKey !== key) {
        this.sortKey = key
        this.sortDir = DESC_FIRST_SORT_KEYS.has(key) ? 'desc' : 'asc'
      } else {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      }
    },

    selectNgWord(word: string | null) {
      this.selectedNgWord = word
    },
    toggleChecked(sellerId: string, asin: string) {
      const key = `${sellerId}:${asin}`
      if (this.checkedKeys.has(key)) this.checkedKeys.delete(key)
      else this.checkedKeys.add(key)
    },
    /** チェックを付ける(切り替えない)。全選択で、同じ出品が複数行あっても外れないように使う */
    setChecked(sellerId: string, asin: string) {
      this.checkedKeys.add(`${sellerId}:${asin}`)
    },
    clearChecked() {
      this.checkedKeys.clear()
    },
    checkedTargets(): { sellerId: string; asin: string }[] {
      return [...this.checkedKeys].map((key) => {
        const [sellerId, asin] = key.split(':')
        return { sellerId, asin }
      })
    },
  },
})
